import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

import type { richTextField_updateWorkItemFieldsActionRenderer$key } from './__generated__/richTextField_updateWorkItemFieldsActionRenderer.graphql';

const messages = defineMessages({
	updateAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.rich-text-field.update-action',
		defaultMessage: 'Update {fieldName}',
		description: 'Label for the change action in the rich text field',
	},
	clearAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.rich-text-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the rich text field',
	},
});

export type Props = {
	queryRef: richTextField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const RichTextField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment richTextField_updateWorkItemFieldsActionRenderer on JiraRichTextField {
				fieldId
				name
				richText {
					adfValue {
						convertedPlainText {
							plainText
							isTruncated
						}
					}
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === action?.data?.fieldKey;

	const isUpdatingAction = Boolean(action?.data?.fieldValue);

	const toolTipContentProvider = useCallback(() => {
		if (!isFieldRenderable) {
			return null;
		}

		const currentPlainText = data?.richText?.adfValue?.convertedPlainText?.plainText;
		const isTruncated = data?.richText?.adfValue?.convertedPlainText?.isTruncated;

		const current = currentPlainText ? `${currentPlainText}${isTruncated ? '...' : ''}` : undefined;

		const suggested =
			action?.data?.fieldValue !== undefined && action?.data?.fieldValue !== null
				? String(action.data.fieldValue)
				: undefined;

		return (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				current={current}
				suggested={suggested}
				fieldName={data?.name}
			/>
		);
	}, [
		action.data.fieldValue,
		action.data?.reasoning,
		data?.name,
		data?.richText?.adfValue?.convertedPlainText?.isTruncated,
		data?.richText?.adfValue?.convertedPlainText?.plainText,
		isFieldRenderable,
	]);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!isFieldRenderable) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.rich-text-field"
		>
			{formatMessage(isUpdatingAction ? messages.updateAction : messages.clearAction, {
				fieldName: data?.name?.toLowerCase(),
			})}
		</Text>
	);
};
